/**
 * `<Icon>` — Lucide-style stroke icons (UX-reconciliation port from the standalone's
 * design source — `docs/refactor/_extracted-standalone-source/01_ed93a966.jsx`).
 *
 * Self-contained: each icon is a typed SVG path set keyed by `IconName`. No external
 * icon library (lucide-react / phosphor-react / similar) — keeps the bundle small and
 * the typography consistent (every icon shares the same stroke width, viewBox, and
 * line-cap/join semantics, so they sit cleanly alongside the type at any size).
 *
 * Usage: `<Icon name="search" />` (default size=16, stroke=1.6).
 *
 * Adding an icon: extend the {@link ICON_PATHS} dict + the {@link IconName} union.
 * Stick to a 24×24 viewBox with `stroke="currentColor"` and round line-caps so new
 * additions match the existing set visually.
 */

import type { CSSProperties, JSX } from 'react';

/**
 * The icon catalogue — verbatim from the standalone's `ICON_PATHS` map. Every value is
 * a Fragment of `<path>` / `<circle>` / `<rect>` / `<ellipse>` elements. Adding a new
 * icon: add a key here AND a literal-string entry to `ICON_NAMES`.
 */
const ICON_PATHS: Record<string, JSX.Element> = {
  edit: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  browse: (
    <>
      <path d="M3 7h7v7H3z" />
      <path d="M14 7h7v4h-7z" />
      <path d="M14 14h7v6h-7z" />
      <path d="M3 17h7v3H3z" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  graph: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <path d="M7.7 7.7l3 8.6M16.3 7.7l-3 8.6M8 6h8" />
    </>
  ),
  health: <path d="M3 12h4l2-6 4 12 2-6h6" />,
  source: <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7L10 5H5a2 2 0 00-2 2z" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </>
  ),
  chat: <path d="M21 12a8 8 0 01-11.6 7.1L4 21l1.9-5.4A8 8 0 1121 12z" />,
  collapse: <path d="M15 18l-6-6 6-6" />,
  expand: <path d="M9 6l6 6-6 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  star: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </>
  ),
  folder: <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />,
  chevron_right: <path d="M9 18l6-6-6-6" />,
  chevron_down: <path d="M6 9l6 6 6-6" />,
  trash: <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6v14a2 2 0 002 2h8a2 2 0 002-2V6" />,
  link: (
    <>
      <path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7L12 19" />
    </>
  ),
  bold: <path d="M7 4h7a4 4 0 010 8H7zM7 12h8a4 4 0 010 8H7z" />,
  italic: <path d="M19 4h-9M14 20H5M15 4L9 20" />,
  heading: <path d="M6 4v16M18 4v16M6 12h12" />,
  list: (
    <>
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
      <path d="M9 6h12M9 12h12M9 18h12" />
    </>
  ),
  numbered: <path d="M9 6h12M9 12h12M9 18h12M4 4v4M3 4h2M3 8h3M3 14h2.5a1.5 1.5 0 010 3H4l2-3M4 20h2" />,
  code: <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />,
  quote: (
    <>
      <path d="M9 7H5a2 2 0 00-2 2v3a2 2 0 002 2h2v3a2 2 0 01-2 2" />
      <path d="M19 7h-4a2 2 0 00-2 2v3a2 2 0 002 2h2v3a2 2 0 01-2 2" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </>
  ),
  hashtag: <path d="M4 9h16M4 15h16M10 3L8 21M16 3l-2 18" />,
  arrow_right: <path d="M5 12h14M13 5l7 7-7 7" />,
  check: <path d="M5 13l4 4L19 7" />,
  x: <path d="M18 6L6 18M6 6l12 12" />,
  filter: <path d="M4 5h16l-6 8v7l-4-2v-5L4 5z" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 11-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  inbox: (
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" />
    </>
  ),
  send: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 14h3M20 9h3M20 14h3" />
    </>
  ),
  zap: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
  key: (
    <>
      <circle cx="7" cy="16" r="3" />
      <path d="M9.5 13.5L21 2M14 7l3 3M17.5 4.5l3 3" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
  layers: (
    <>
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="3" width="18" height="4" rx="1" />
      <path d="M5 7v12a2 2 0 002 2h10a2 2 0 002-2V7M10 12h4" />
    </>
  ),
  export: <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5-5 5 5M12 5v12" />,
  bell: (
    <>
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
  route: (
    <>
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M6 17V8a3 3 0 013-3h6M18 7v9a3 3 0 01-3 3H9" />
    </>
  ),
};

/**
 * Stable string-literal union of every icon name. Keep in sync with
 * {@link ICON_PATHS} — TypeScript will flag any drift at compile time because
 * `<Icon name>` is typed against this union.
 */
export type IconName =
  | 'edit'
  | 'search'
  | 'browse'
  | 'dashboard'
  | 'graph'
  | 'health'
  | 'source'
  | 'settings'
  | 'chat'
  | 'collapse'
  | 'expand'
  | 'plus'
  | 'star'
  | 'more'
  | 'folder'
  | 'chevron_right'
  | 'chevron_down'
  | 'trash'
  | 'link'
  | 'bold'
  | 'italic'
  | 'heading'
  | 'list'
  | 'numbered'
  | 'code'
  | 'quote'
  | 'table'
  | 'hashtag'
  | 'arrow_right'
  | 'check'
  | 'x'
  | 'filter'
  | 'calendar'
  | 'clock'
  | 'refresh'
  | 'warning'
  | 'inbox'
  | 'database'
  | 'send'
  | 'cpu'
  | 'zap'
  | 'key'
  | 'sun'
  | 'layers'
  | 'archive'
  | 'export'
  | 'bell'
  | 'info'
  | 'route';

interface IconProps {
  name: IconName;
  /** Pixel size of the rendered SVG (default 16 — sits on a 16px line height). */
  size?: number;
  /** Stroke width — defaults to 1.6 (matches the standalone). */
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

export function Icon({
  name,
  size = 16,
  strokeWidth = 1.6,
  className = '',
  style,
}: IconProps): JSX.Element | null {
  const paths = ICON_PATHS[name];
  if (paths === undefined) return null;
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

/** The runtime list of icon names — used by boundary tests + dev tooling. */
export const ICON_NAMES: ReadonlySet<IconName> = new Set(Object.keys(ICON_PATHS) as IconName[]);
